import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'core/store';
import {
  IonPage,
  IonContent,
  IonModal,
  IonButton,
  IonToolbar,
  IonRow,
  IonBreadcrumbs,
  IonBreadcrumb
} from '@ionic/react';
import InventoryProductListRowDesktop from 'features/shared/inventory/components/InventoryProductListRowDesktop';
import InventoryProductListRowMobile from 'features/shared/inventory/components/InventoryProductListRowMobile';
import SearchBar, { filterItems } from 'components/searchbar/SearchBar';
import { HKPlatform } from 'core/constants';
import BackButtonHeader from 'core/layout/BackButtonHeader';
import LoadingIonList from 'core/layout/loading-ion-list/LoadingIonList';
import { InventoryItem } from 'core/types';
import { useHistory, useParams } from 'react-router';
import InventoryDetailsMobile from 'features/shared/inventory/components/InventoryDetailsMobile';
import './InventoryProducts.scss';
import InventoryAddProduct from 'features/shared/inventory/components/InventoryAddProduct';
import {
  showAddItemModal,
  hideAddItemModal,
  hideItemDetailsModal,
  showItemDetailsModal,
  resetInventoryDetails,
  showDesktopItemDetails,
  ProductGroup
} from 'features/shared/inventory/store/InventorySlice';
import {
  requestProductGroup,
  requestProductGroupCount
} from 'features/shared/inventory/store/InventoryActions';
import { gaBtnClickEvent } from 'core/util';
import { getFallbackProductImage } from 'core/util/product-images';
import { formatInventoryFieldValue } from 'features/shared/inventory/utils/formatInventoryFieldValue';
interface ItemParams {
  id?: string | undefined;
}

const InventoryProducts: React.FC = () => {
  const { id } = useParams<ItemParams>();
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const {
    products,
    productGroups,
    productGroupLoading,
    addItemModalVisible,
    addItemShowLanding,
    itemDetailsModalVisible
  } = useSelector((state: RootState) => state.inventory);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState<ProductGroup>({
    id: 0,
    count: 0,
    group: ''
  });
  const history = useHistory();
  const dispatch = useDispatch();

  const filteredInventory = filterItems(
    products || [],
    [
      'product_template.manufacturer_name',
      'product_template.type',
      'product_template.model',
      'name',
      'serial',
      'attributes.colorgroupdesc',
      'attributes.colordesc'
    ],
    search
  );

  const showDetailsModal = (id: number) => {
    dispatch(showItemDetailsModal(id));
  };

  const closeDetailsModal = () => {
    if (!!homeId) {
      dispatch(requestProductGroupCount(homeId));
    }
    dispatch(hideItemDetailsModal());
  };

  const openAddItemModal = () => {
    gaBtnClickEvent('add_item');
    dispatch(showAddItemModal());
  };

  const closeAddItemModal = () => {
    if (!!homeId) {
      dispatch(requestProductGroupCount(homeId));
    }
    dispatch(hideAddItemModal());
  };

  const goToInventoryDetailsPage = (productId: number) => {
    if (id !== undefined) {
      dispatch(resetInventoryDetails());
      dispatch(showDesktopItemDetails(productId));
      history.push(`/inventory/group/${id}/product/${productId}`);
    }
  };

  const imageForItem = (item: InventoryItem): string => {
    if (!!item.image) {
      return item.image;
    }
    if (!!item.product_template.manufacturer_image) {
      return item.product_template.manufacturer_image;
    } else {
      return getFallbackProductImage(item.product_template.type);
    }
  };

  useEffect(() => {
    if (!!homeId && !!id) {
      dispatch(requestProductGroup(homeId, id));
    }
  }, [dispatch, homeId, id]);

  useEffect(() => {
    if (!productGroups || id === undefined) {
      return;
    }
    const g = productGroups.find((pg) => pg.id === Number(id));
    if (g) {
      setGroup(g);
    }
  }, [productGroups, id]);

  function DesktopInventoryProductsView() {
    return (
      <>
        <IonRow className="hk-desktop-digital-inventory-products-header">
          <div className="hk-desktop-digital-inventory-products-header-container">
            <IonBreadcrumbs className="hk-desktop-digital-inventory-products-header-breadcrumbs">
              <IonBreadcrumb href="/inventory">Inventory</IonBreadcrumb>
              <IonBreadcrumb>{group.group}</IonBreadcrumb>
            </IonBreadcrumbs>
            <div className="hk-desktop-digital-inventory-products-header-right">
              <div className="hk-desktop-digital-inventory-products-search">
                <SearchBar
                  value={search}
                  disabled={products?.length === 0}
                  onSearchChange={(e) => setSearch(e)}
                ></SearchBar>
              </div>
              <div className="hk-desktop-digital-inventory-products-header-button">
                <IonButton
                  expand="block"
                  color="primary"
                  strong={true}
                  onClick={() => openAddItemModal()}
                >
                  Add Item
                </IonButton>
              </div>
            </div>
          </div>
        </IonRow>
        <IonContent className="hk-desktop-digital-inventory-products">
          <LoadingIonList
            className="hk-desktop-digital-inventory-products-list"
            label={'Items In Inventory'}
            description={'There are no items of this kind added yet.'}
            loading={productGroupLoading}
            items={filteredInventory}
          >
            {(filteredInventory as InventoryItem[]).map((product) => {
              const itemId = product.id;
              const { name, product_template } = product;
              const image = imageForItem(product);
              return (
                <InventoryProductListRowDesktop
                  key={itemId}
                  image={image}
                  title={!!name ? name : product_template.name}
                  modelDisplay={
                    'Model: ' +
                    formatInventoryFieldValue(product_template.model)
                  }
                  brandDisplay={
                    'Brand: ' +
                    formatInventoryFieldValue(
                      product_template.manufacturer_name
                    )
                  }
                  onClick={() => {
                    goToInventoryDetailsPage(itemId);
                  }}
                />
              );
            })}
          </LoadingIonList>
        </IonContent>
      </>
    );
  }

  function MobileInventoryProductsView() {
    return (
      <>
        <BackButtonHeader title={group.group} />
        <IonContent fullscreen>
          <SearchBar
            value={search}
            disabled={products?.length === 0}
            onSearchChange={(e) => setSearch(e)}
          ></SearchBar>
          <LoadingIonList
            label={'Items In Inventory'}
            description={'There are no items of this kind added yet.'}
            loading={productGroupLoading}
            items={filteredInventory}
            className="hk-digital-inventory-products-list"
          >
            {(filteredInventory as InventoryItem[]).map((product) => {
              const { id } = product;
              const { name, product_template } = product;
              const image = imageForItem(product);
              return (
                <InventoryProductListRowMobile
                  key={id}
                  image={image}
                  title={!!name ? name : product_template.name}
                  modelDisplay={
                    'Model: ' +
                    formatInventoryFieldValue(product_template.model)
                  }
                  brandDisplay={
                    'Brand: ' +
                    formatInventoryFieldValue(
                      product_template.manufacturer_name
                    )
                  }
                  onClick={() => {
                    showDetailsModal(id);
                  }}
                />
              );
            })}
          </LoadingIonList>
        </IonContent>
        <IonToolbar className="hk-mobile-digital-inventory-products-toolbar">
          <IonButton
            expand="block"
            color="primary"
            strong={true}
            onClick={() => openAddItemModal()}
          >
            Add Item
          </IonButton>
        </IonToolbar>
        <IonModal
          className="hk-digital-inventory-details-modal"
          isOpen={itemDetailsModalVisible}
          onDidDismiss={() => {
            closeDetailsModal();
          }}
        >
          <InventoryDetailsMobile />
        </IonModal>
      </>
    );
  }

  return (
    <IonPage
      className={
        platformType === HKPlatform.DESKTOP && isDesktopWidth
          ? 'hk-desktop-digital-inventory-products-page'
          : ''
      }
    >
      {platformType === HKPlatform.DESKTOP && isDesktopWidth
        ? DesktopInventoryProductsView()
        : MobileInventoryProductsView()}
      <IonModal
        className={`hk-digital-inventory-add-product-modal${
          addItemShowLanding ? ' hk-inventory-add-item-landing-active' : ''
        }`}
        isOpen={addItemModalVisible}
        backdropDismiss={false}
        onDidDismiss={() => {
          closeAddItemModal();
        }}
      >
        <InventoryAddProduct />
      </IonModal>
    </IonPage>
  );
};

export default InventoryProducts;
